import * as THREE from 'three'
import Experience from '../../Experience'
import vertexShader from './vertex.glsl'
import fragmentShader from './fragment.glsl'

/**
 * Fireflies — 焚き火のまわりを漂う火の粉/蛍。
 * 焚き火(-3.4, 0.3, 16)を中心とした扁平ドーム内にランダム配置し、
 * 頂点シェーダーで個体ごとに位相の異なる漂いを与える。1ドローコール。
 * uGlobalAlpha は SceneDirector がセクションごとに調整する。
 */
export default class Fireflies {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.sizes = this.experience.sizes
    this.quality = this.experience.quality
    this.debug = this.experience.debug

    this.count = this.quality.tier === 'low' ? 25 : 60
    this.center = new THREE.Vector3(-3.4, 0.3, 16)
    this.radius = 2.5

    this.setGeometry()
    this.setMaterial()
    this.setPoints()
    this.setDebug()

    this.time.on('tick', () => {
      this.update()
    })

    this.sizes.on('resize', () => {
      this.material.uniforms.uPixelRatio.value = Math.min(this.sizes.pixelRatio, this.quality.maxPixelRatio)
    })
  }

  setGeometry() {
    this.geometry = new THREE.BufferGeometry()

    const positions = new Float32Array(this.count * 3)
    const scales = new Float32Array(this.count)
    const offsets = new Float32Array(this.count)

    for (let i = 0; i < this.count; i++) {
      // 扁平ドーム内のランダム位置（上に行くほど狭まる）
      const angle = Math.random() * Math.PI * 2
      const r = Math.pow(Math.random(), 0.6) * this.radius
      const height = Math.random() * 1.8

      positions[i * 3 + 0] = this.center.x + Math.cos(angle) * r * (1.0 - height * 0.25)
      positions[i * 3 + 1] = this.center.y + height
      positions[i * 3 + 2] = this.center.z + Math.sin(angle) * r * (1.0 - height * 0.25)

      scales[i] = 0.5 + Math.random()
      offsets[i] = Math.random()
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    this.geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    this.geometry.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1))
  }

  setMaterial() {
    this.material = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uTime: new THREE.Uniform(0),
        uPixelRatio: new THREE.Uniform(Math.min(this.sizes.pixelRatio, this.quality.maxPixelRatio)),
        uSize: new THREE.Uniform(120),
        uGlobalAlpha: new THREE.Uniform(0.6),
        uColor: new THREE.Uniform(new THREE.Color('#ffb46b'))
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
  }

  setPoints() {
    this.points = new THREE.Points(this.geometry, this.material)
    this.points.renderOrder = 3 // 煙・炎（renderOrder 0 / 深度書き込みなし）より後に描く
    this.scene.add(this.points)
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Fireflies')
    this.debugFolder.add(this.material.uniforms.uSize, 'value')
      .min(0).max(400).step(1).name('Size')
    this.debugFolder.add(this.material.uniforms.uGlobalAlpha, 'value')
      .min(0).max(1).step(0.01).name('Global Alpha')
    this.debugFolder.addColor(this.material.uniforms.uColor, 'value').name('Color')
    this.debugFolder.close()
  }

  update() {
    // Time.elapsed は秒単位
    this.material.uniforms.uTime.value = this.time.elapsed
  }

  dispose() {
    this.scene.remove(this.points)
    this.geometry.dispose()
    this.material.dispose()
  }
}
